import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, Phase } from '../lib/api';
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  SparklesIcon,
  PlusIcon,
  XMarkIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export default function PhaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState('');

  // Phase 1: practice description intake (before run)
  const [practiceDescription, setPracticeDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Phase 4: specialty profile intake (before run)
  const [specialties, setSpecialties] = useState<string[]>(['']);
  const [isSavingSpecialties, setIsSavingSpecialties] = useState(false);

  // Phase 4: add more specialty profiles after completion
  const [postSpecialties, setPostSpecialties] = useState<string[]>(['']);
  const [isAddingSpecialties, setIsAddingSpecialties] = useState(false);
  const [addSpecialtiesSuccess, setAddSpecialtiesSuccess] = useState(false);

  useEffect(() => {
    loadPhase();
  }, [slug]);

  const loadPhase = async () => {
    if (!slug) return;

    try {
      const response = await api.getPhaseDetail(slug);
      setPhase(response);
    } catch (error) {
      console.error('Failed to load phase:', error);
      setError('Failed to load phase details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartPhase = async () => {
    if (!slug || !phase) return;

    setIsExecuting(true);
    setError('');

    try {
      await api.startPhase(slug);
      await loadPhase();
      navigate(`/phases/${slug}/review`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start phase');
    } finally {
      setIsExecuting(false);
    }
  };

  // ── Phase 1 practice description helpers ─────────────────────────────────
  const handleSaveAndStartPhase1 = async () => {
    if (!slug) return;
    setIsSavingDescription(true);
    setError('');
    try {
      if (practiceDescription.trim()) {
        await api.submitPhaseInput(slug, { practice_description: practiceDescription.trim() });
      }
      setIsExecuting(true);
      await api.startPhase(slug);
      await loadPhase();
      navigate(`/phases/${slug}/review`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save description or start phase');
    } finally {
      setIsSavingDescription(false);
      setIsExecuting(false);
    }
  };

  // ── Phase 4 specialty helpers ────────────────────────────────────────────
  const addSpecialty = () => setSpecialties(prev => [...prev, '']);

  const updateSpecialty = (index: number, value: string) =>
    setSpecialties(prev => prev.map((s, i) => (i === index ? value : s)));

  const removeSpecialty = (index: number) =>
    setSpecialties(prev => prev.filter((_, i) => i !== index));

  const handleSaveAndStartPhase4 = async () => {
    if (!slug) return;
    setIsSavingSpecialties(true);
    setError('');
    try {
      // Save specialties to client record
      const validSpecialties = specialties.filter(s => s.trim() !== '');
      await api.submitPhaseInput(slug, { specialties: validSpecialties });
      // Start phase
      setIsExecuting(true);
      await api.startPhase(slug);
      await loadPhase();
      navigate(`/phases/${slug}/review`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save specialties or start phase');
    } finally {
      setIsSavingSpecialties(false);
      setIsExecuting(false);
    }
  };

  const handleAddSpecialties = async () => {
    if (!slug) return;
    const valid = postSpecialties.filter(s => s.trim() !== '');
    if (!valid.length) return;
    setIsAddingSpecialties(true);
    setError('');
    try {
      await api.addSpecialties(slug, valid);
      setAddSpecialtiesSuccess(true);
      setPostSpecialties(['']);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate specialty profiles');
    } finally {
      setIsAddingSpecialties(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!phase) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Phase not found</p>
        <Link to="/" className="btn btn-primary mt-4">
          Return to dashboard
        </Link>
      </div>
    );
  }

  const statusColor = phase.status === 'completed' ? 'sage' : phase.status === 'available' ? 'teal' : phase.status === 'pending_review' ? 'copper' : 'charcoal';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-charcoal-300 hover:text-copper-600 transition-colors text-sm">
        <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
        Back to dashboard
      </Link>

      {/* Phase Header */}
      <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-[radial-gradient(circle,rgba(196,117,76,0.05),transparent_70%)]" />
          <svg className="absolute right-4 top-4 w-24 h-24 opacity-[0.03]" viewBox="0 0 100 100">
            <circle cx="80" cy="20" r="40" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="80" cy="20" r="25" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
          </svg>
        </div>

        {/* Accent strip */}
        <div className={`h-1 bg-gradient-to-r ${
          statusColor === 'sage' ? 'from-sage-400 to-sage-500' :
          statusColor === 'teal' ? 'from-teal-400 to-teal-500' :
          statusColor === 'copper' ? 'from-copper-400 to-copper-500' :
          'from-charcoal-200 to-charcoal-300'
        }`} />

        <div className="relative p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-2 bg-charcoal-50 rounded-full px-3 py-1 text-xs font-semibold text-charcoal-400 mb-3">
                <span className={`w-1.5 h-1.5 rounded-full bg-${statusColor}-500`} />
                Phase {phase.number}
              </div>
              <h1 className="text-3xl font-bold text-charcoal-500">{phase.name}</h1>
            </div>
            {phase.status === 'completed' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage-50 border border-sage-200 text-sage-600 text-sm font-medium">
                <CheckCircleIcon className="h-4 w-4" />
                Completed
              </span>
            )}
          </div>

          <p className="text-charcoal-400 text-lg leading-relaxed">{phase.description}</p>



          {/* Dependencies */}
          {phase.depends_on && phase.depends_on.length > 0 && (
            <div className="mt-4 p-4 bg-cream-50 rounded-xl border border-cream-200">
              <p className="text-sm text-charcoal-400">
                <strong className="text-charcoal-500">Requires:</strong> Complete Phase{phase.depends_on.length > 1 ? 's' : ''} {phase.depends_on.join(', ')} first
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex items-center gap-4">
            {phase.status === 'available' && phase.number !== 1 && phase.number !== 4 && (
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={handleStartPhase}
                  disabled={isExecuting}
                  className="btn btn-primary flex items-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
                >
                  {isExecuting ? (
                    <>
                      <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                      Executing agents...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-5 w-5 mr-2" />
                      Start Phase
                    </>
                  )}
                </button>
                <p className="text-xs text-charcoal-300">{phase.revision_budget} revision{phase.revision_budget !== 1 ? 's' : ''} included</p>
              </div>
            )}

            {phase.status === 'available' && phase.number === 1 && (
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={handleSaveAndStartPhase1}
                  disabled={isSavingDescription || isExecuting}
                  className="btn btn-primary flex items-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
                >
                  {isSavingDescription || isExecuting ? (
                    <>
                      <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                      {isSavingDescription ? 'Saving...' : 'Executing agents...'}
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-5 w-5 mr-2" />
                      Save & Start Phase
                    </>
                  )}
                </button>
                <p className="text-xs text-charcoal-300">{phase.revision_budget} revision{phase.revision_budget !== 1 ? 's' : ''} included</p>
              </div>
            )}

            {phase.status === 'available' && phase.number === 4 && (
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={handleSaveAndStartPhase4}
                  disabled={isSavingSpecialties || isExecuting}
                  className="btn btn-primary flex items-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
                >
                  {isSavingSpecialties || isExecuting ? (
                    <>
                      <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                      {isSavingSpecialties ? 'Saving specialties...' : 'Executing agents...'}
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-5 w-5 mr-2" />
                      Save & Start Phase
                    </>
                  )}
                </button>
                <p className="text-xs text-charcoal-300">{phase.revision_budget} revision{phase.revision_budget !== 1 ? 's' : ''} included</p>
              </div>
            )}

            {phase.status === 'completed' && (
              <Link to={`/phases/${slug}/review`} className="btn btn-primary flex items-center shadow-sm shadow-copper-500/20">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                View Content
              </Link>
            )}

            {phase.status === 'locked' && (
              <button disabled className="btn btn-secondary opacity-50 cursor-not-allowed">
                Phase Locked
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Phase 1: Practice Description Intake Form */}
      {phase.number === 1 && phase.status === 'available' && (
        <div className="bg-white rounded-2xl border border-charcoal-100 shadow-sm">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <DocumentTextIcon className="h-5 w-5 text-copper-400" />
              <h2 className="text-xl font-bold text-charcoal-500">Tell Us About Your Practice</h2>
            </div>
            <p className="text-sm text-charcoal-400 mb-5">
              Describe what makes your practice unique — your specialty, approach, philosophy, or the types of patients you focus on.
              This helps our AI agents generate content that's accurate to your specific practice, especially when your website can't be automatically read.
            </p>
            <textarea
              value={practiceDescription}
              onChange={e => setPracticeDescription(e.target.value)}
              rows={5}
              placeholder="e.g. We are a brain-based, neurologically-focused chiropractic clinic specializing in helping patients with chronic headaches, migraines, and nervous system dysfunction. We use advanced spinal correction techniques and functional neurology protocols rather than traditional adjustment-only care..."
              className="w-full px-4 py-3 rounded-xl border border-charcoal-200 text-charcoal-500 placeholder-charcoal-300 text-sm focus:outline-none focus:ring-2 focus:ring-copper-300 focus:border-transparent resize-none"
            />
            <p className="mt-2 text-xs text-charcoal-300">
              Optional — you can skip this and start immediately, but more detail means better-targeted content.
            </p>
          </div>
        </div>
      )}

      {/* Phase 4: Specialty Intake Form */}
      {phase.number === 4 && phase.status === 'available' && (
        <div className="bg-white rounded-2xl border border-charcoal-100 shadow-sm">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <UserGroupIcon className="h-5 w-5 text-copper-400" />
              <h2 className="text-xl font-bold text-charcoal-500">Specialty Client Profiles</h2>
            </div>
            <p className="text-sm text-charcoal-400 mb-5">
              Optionally list any specialty services or patient types you'd like separate ideal client profiles for.
              Leave blank to generate just the primary profile. You can add as many as you need.
            </p>

            <div className="space-y-3">
              {specialties.map((specialty, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={specialty}
                    onChange={e => updateSpecialty(index, e.target.value)}
                    placeholder="e.g. Sports Rehabilitation, Pediatric Care, Weight Loss..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-charcoal-200 text-charcoal-500 placeholder-charcoal-300 text-sm focus:outline-none focus:ring-2 focus:ring-copper-300 focus:border-transparent"
                  />
                  {specialties.length > 1 && (
                    <button
                      onClick={() => removeSpecialty(index)}
                      className="p-2 text-charcoal-300 hover:text-red-400 transition-colors rounded-lg hover:bg-red-50"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={addSpecialty}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-copper-600 hover:text-copper-700 font-medium transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Add Another Specialty
            </button>
          </div>
        </div>
      )}

      {/* Phase 4 (completed): Add more specialty profiles */}
      {phase.number === 4 && phase.status === 'completed' && (
        <div className="bg-white rounded-2xl border border-charcoal-100 shadow-sm">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <UserGroupIcon className="h-5 w-5 text-copper-400" />
              <h2 className="text-xl font-bold text-charcoal-500">Add Specialty Profiles</h2>
            </div>
            <p className="text-sm text-charcoal-400 mb-5">
              Now that you've seen your primary profile, you can generate additional specialty profiles
              for specific services or patient types. Each one will be tailored to that segment.
            </p>

            {addSpecialtiesSuccess && (
              <div className="mb-4 flex items-center gap-2 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3 text-sm text-sage-700">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                Specialty profiles are being generated — check the review page shortly.
              </div>
            )}

            <div className="space-y-3">
              {postSpecialties.map((specialty, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={specialty}
                    onChange={e => setPostSpecialties(prev => prev.map((s, i) => (i === index ? e.target.value : s)))}
                    placeholder="e.g. Sports Rehabilitation, Pediatric Care, Weight Loss..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-charcoal-200 text-charcoal-500 placeholder-charcoal-300 text-sm focus:outline-none focus:ring-2 focus:ring-copper-300 focus:border-transparent"
                  />
                  {postSpecialties.length > 1 && (
                    <button
                      onClick={() => setPostSpecialties(prev => prev.filter((_, i) => i !== index))}
                      className="p-2 text-charcoal-300 hover:text-red-400 transition-colors rounded-lg hover:bg-red-50"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => setPostSpecialties(prev => [...prev, ''])}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-copper-600 hover:text-copper-700 font-medium transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Add Another Specialty
            </button>

            <div className="mt-5">
              <button
                onClick={handleAddSpecialties}
                disabled={isAddingSpecialties || postSpecialties.every(s => !s.trim())}
                className="btn btn-primary flex items-center disabled:opacity-50 shadow-sm shadow-copper-500/20"
              >
                {isAddingSpecialties ? (
                  <>
                    <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                    Generating profiles...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Generate Specialty Profiles
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase Deliverables */}
      {phase.deliverables && phase.deliverables.length > 0 && (
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm">
          <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none opacity-[0.03]">
            <svg viewBox="0 0 800 40" preserveAspectRatio="none" className="w-full h-full">
              <path d="M0,40 L0,25 L100,12 L200,22 L300,8 L400,18 L500,5 L600,20 L700,10 L800,30 L800,40 Z" fill="#2C3E3D" />
            </svg>
          </div>

          <div className="relative p-6">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="h-5 w-5 text-copper-400" />
              <h2 className="text-xl font-bold text-charcoal-500">What You'll Get</h2>
            </div>
            <ul className="space-y-3">
              {phase.deliverables.map((deliverable, index) => (
                <li key={index} className="flex items-start gap-3 p-2 rounded-lg hover:bg-cream-50 transition-colors">
                  <CheckCircleIcon className="h-5 w-5 text-sage-500 mt-0.5 flex-shrink-0" />
                  <span className="text-charcoal-400">{formatDeliverable(deliverable)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDeliverable(key: string): string {
  const deliverables: Record<string, string> = {
    // Phase 1
    business_profile: 'Practice foundation profile',
    // Phase 2
    scout_report: 'Comprehensive business intelligence report',
    seo_report: 'Local SEO competitive analysis',
    google_reviews: 'Google Reviews sentiment analysis',
    compliance_profile: 'Regulatory compliance profile',
    // Phase 3
    brand_guide: 'Complete brand style guide',
    // Phase 4
    primary_icp: 'Primary ideal client profile',
    specialty_profiles: 'Specialty audience profiles',
    // Phase 5
    captured_images: 'Professional practice photography',
    generated_images: 'AI-generated brand imagery',
    // Phase 6
    website_pages: '12-page SEO-optimized website',
    funnel_pages: '4-step conversion funnel',
    // Phase 7
    blog_calendar: '12-month blog content calendar',
    blog_posts: 'SEO-optimized blog posts',
    // Phase 8
    social_calendar: 'Social media posting schedule',
    social_posts: 'Ready-to-publish social content',
    // Phase 9
    email_sequences: 'Automated email campaigns',
    sms_campaigns: 'SMS marketing templates',
    review_requests: 'Patient review request campaigns',
    // Phase 10
    activation_package: 'Complete system deployment',
  };

  return deliverables[key] || key;
}
